import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  OnInit,
  inject,
  Renderer2,
  ElementRef,
  AfterViewInit,
  ViewChild,
  HostBinding,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule } from '@jsverse/transloco';
import { BoelsUser, UserEnum, AppConfig } from '@boels/models';
import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { trigger, transition, style, animate } from '@angular/animations';
import { ToggleLanguageSelectorComponent } from '../../toggle-language-selector/toggle-language-selector.component';
import { QaHooksDirective } from '@boels/helpers';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoginComponent } from '../login/login.component';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  faFileContract,
  faFileInvoice,
  faGlobe,
  faRightFromBracket,
  faUser,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

type MenuItem = {
  icon?: Readonly<IconDefinition>;
  link?: string;
  imgSrc?: string;
  title?: string;
  subtitle?: string;
  subtitleBinding?: 'debtor-name';
  showLanguageSelector?: boolean;
  logout?: boolean;
  shouldHaveDebtorData?: boolean;
  iconClass?: string;
};

const MENUTITEMS: MenuItem[] = [
  {
    title: 'header.auth.account',
    icon: faUser,
    link: 'account',
    subtitleBinding: 'debtor-name',
    iconClass: 'account-icon',
  },
  {
    title: 'header.auth.countryLanguage',
    icon: faGlobe,
    showLanguageSelector: true,
    iconClass: 'language-icon',
  },
  {
    subtitle: 'header.auth.product',
    link: 'products',
    imgSrc: 'assets/images/icons/my-products.svg',
    shouldHaveDebtorData: true,
    iconClass: 'products-icon',
  },
  {
    subtitle: 'header.auth.contract',
    link: 'contracts',
    icon: faFileContract,
    shouldHaveDebtorData: true,
    iconClass: 'contracts-icon',
  },
  {
    subtitle: 'header.auth.invoice',
    link: 'invoices',
    icon: faFileInvoice,
    shouldHaveDebtorData: true,
    iconClass: 'invoices-icon',
  },
  {
    subtitle: 'header.auth.logout',
    icon: faRightFromBracket,
    logout: true,
    iconClass: 'logout-icon',
  },
];

@Component({
  selector: 'boels-account-mobile',
  templateUrl: './account-mobile.component.html',
  styleUrls: ['./account-mobile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    TranslocoModule,
    ToggleLanguageSelectorComponent,
    OverlayModule,
    ScrollingModule,
    QaHooksDirective,
    FaIconComponent,
    LoginComponent,
  ],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '200ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateX(100%)', opacity: 0 }),
        ),
      ]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMobileComponent implements OnInit, AfterViewInit {
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly environment = inject('ENVIRONMENT' as any) as AppConfig;

  @ViewChild('closeButton') closeButton!: ElementRef;
  @ViewChild('accountContent') accountContent!: ElementRef<HTMLElement>;
  @ViewChild('topSentinel') topSentinel!: ElementRef;
  @ViewChild('bottomSentinel') bottomSentinel!: ElementRef;

  boelsUser = input.required<BoelsUser>();
  isActive = input<boolean>(false);
  activeCulture = input.required<string | undefined>();

  closeModuleEvent = output<void>();
  login = output<void>();
  logout = output<void>();
  openLanguageSelector = output<void>();

  @HostBinding('class.is-visible') get isVisible() {
    return this.isActive();
  }

  protected readonly userType = UserEnum;
  protected readonly menuItemsList: MenuItem[] = MENUTITEMS;

  private mainContent: HTMLElement | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private isAnimating = false;
  private topObserver?: IntersectionObserver;
  private bottomObserver?: IntersectionObserver;

  constructor() {
    this.setupDestroyHandlers();
    this.setupIsActiveSubscription();
  }

  private setupDestroyHandlers(): void {
    this.destroyRef.onDestroy(() => {
      this.enableMainContent();
      this.disconnectObservers();
    });
  }

  private setupIsActiveSubscription(): void {
    toObservable(this.isActive)
      .pipe(takeUntilDestroyed())
      .subscribe((isActive) => {
        if (isActive) {
          this.disableMainContent();
        } else {
          this.enableMainContent();
        }
      });
  }

  ngOnInit() {
    if (this.isActive()) {
      this.disableMainContent();
    }

    this.mainContent =
      document.querySelector('main') ||
      document.querySelector('#root') ||
      document.querySelector('.app-root');
  }

  ngAfterViewInit() {
    if (this.isActive() && this.closeButton) {
      setTimeout(() => {
        this.closeButton.nativeElement.focus();
      });
    }

    this.setupScrollObservers();
  }

  closeModule(): void {
    this.enableMainContent();
    this.closeModuleEvent.emit();
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }

  menuItemClicked(menuItem: MenuItem, event: Event): void {
    event.stopPropagation();
    this.closeModule();

    if (menuItem.link) {
      this.openMyBoelsUrl(menuItem.link);
    } else if (menuItem.logout) {
      this.logout.emit();
    } else if (menuItem.showLanguageSelector) {
      this.openLanguageSelector.emit();
    }
  }

  handleMenuItemKeyDown(menuItem: MenuItem, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.menuItemClicked(menuItem, event);
    }
  }

  getSubtitle(subtitleBinding: string): string {
    switch (subtitleBinding) {
      case 'debtor-name':
        return this.boelsUser().debtor?.name as string;
    }
    return '';
  }

  private openMyBoelsUrl(routeSlug: string) {
    let culture = this.activeCulture();
    if (culture === 'hu-hu') {
      culture = 'en-hu';
    }

    window.open(
      this.environment.myBoelsUrl + '/' + routeSlug + '?locale=' + culture,
      '_blank',
    );
  }

  private disableMainContent(): void {
    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.renderer.addClass(document.body, 'modal-open');
    const scrollY = window.scrollY;
    this.renderer.setStyle(document.body, 'position', 'fixed');
    this.renderer.setStyle(document.body, 'top', `-${scrollY}px`);
    this.renderer.setStyle(document.body, 'width', '100%');

    if (this.mainContent) {
      this.renderer.setAttribute(this.mainContent, 'inert', '');
    }
  }

  private enableMainContent(): void {
    this.renderer.removeClass(document.body, 'modal-open');
    const scrollY = document.body.style.top;
    this.renderer.removeStyle(document.body, 'position');
    this.renderer.removeStyle(document.body, 'top');
    this.renderer.removeStyle(document.body, 'width');
    window.scrollTo(0, parseInt(scrollY || '0') * -1);

    if (this.mainContent) {
      this.renderer.removeAttribute(this.mainContent, 'inert');
    }
  }

  private setupScrollObservers() {
    if (!this.accountContent?.nativeElement) return;

    const options: IntersectionObserverInit = {
      root: this.accountContent.nativeElement,
      threshold: [0, 1],
    };

    this.topObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.isActive()) {
            this.triggerAnimation(
              this.accountContent.nativeElement,
              'overscroll-top',
            );
          }
        });
      },
      options,
    );

    this.bottomObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.isActive()) {
            this.triggerAnimation(
              this.accountContent.nativeElement,
              'overscroll-bottom',
            );
          }
        });
      },
      options,
    );

    // Create and observe sentinel elements
    const topSentinel = this.renderer.createElement('div');
    const bottomSentinel = this.renderer.createElement('div');

    this.renderer.setStyle(topSentinel, 'height', '1px');
    this.renderer.setStyle(bottomSentinel, 'height', '1px');

    this.renderer.insertBefore(
      this.accountContent.nativeElement,
      topSentinel,
      this.accountContent.nativeElement.firstChild,
    );
    this.renderer.appendChild(
      this.accountContent.nativeElement,
      bottomSentinel,
    );

    this.topObserver.observe(topSentinel);
    this.bottomObserver.observe(bottomSentinel);
  }

  private disconnectObservers() {
    this.topObserver?.disconnect();
    this.bottomObserver?.disconnect();
  }

  private triggerAnimation(element: HTMLElement, className: string) {
    if (this.isAnimating || !this.isActive()) return;

    this.isAnimating = true;
    this.renderer.addClass(element, className);

    setTimeout(() => {
      if (this.isActive()) {
        this.renderer.removeClass(element, className);
        this.isAnimating = false;
      }
    }, 500);
  }
}
